/**
 * /token-intelligence-suite — PUBLIC landing page.
 * ===================================================
 * Headline marketing page for the 10-feature Token Intelligence Suite.
 * Frames CARTIEAI as the only complete AI-token FinOps platform before
 * Datadog / Helicone consolidate the category.
 *
 * Structure:
 *   - Sticky nav (Sign In + Live Demo CTA)
 *   - Hero: "10 features. 1 platform. Before Datadog notices."
 *   - 10 feature cards (T1-T10) — each shows badge, icon, name, tagline,
 *     access tier (Free / Authed), CTA → relevant page
 *   - Social proof: example dollar savings from each tool (honest numbers)
 *   - Mid-page CTA: "Try the 2 free tools right now"
 *   - FAQ (8 entries — pricing, what's free, when to use which tool)
 *   - Bottom CTA: Start free trial
 *
 * Brand: slate-950 + emerald/cyan accents · NO purple/violet gradients.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  ArrowRight, ArrowLeft, AlertTriangle, Calculator, Database,
  Repeat, Activity, BookOpen, Minimize2, Layers, Pause, Route,
  Lock, Sparkles, Check, ChevronDown, ChevronUp, X, Zap, DollarSign,
} from 'lucide-react';

// ─── Animated count-up hook ─────────────────────────────────────────────
const useCountUp = (target, durationMs = 1600, decimals = 0) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = null;
    let rafId = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min(1, (ts - start) / durationMs);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(target * eased);
      if (progress < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [target, durationMs]);
  return decimals === 0 ? Math.floor(val) : Number(val.toFixed(decimals));
};

const formatNum = (n) => n.toLocaleString();

const FEATURES = [
  {
    id: 'T2', name: 'Prompt Cost Predictor', icon: Calculator,
    tagline: 'Paste any prompt → instant per-model cost across 11 LLMs.',
    detail: 'Calibrated tokenizer + Jan-2026 pricing. Engineers use this before shipping a feature to pick the cheapest model that still works.',
    tier: 'Free · Public', route: '/prompt-cost-predictor',
    cta: 'Try it now', highlight: 'savings: see 90% cheaper alternatives instantly',
  },
  {
    id: 'T7', name: 'Fine-tuning ROI Calculator', icon: BookOpen,
    tagline: '"Should we fine-tune?" — clean crossover math, no signup.',
    detail: 'Monthly cost without/with fine-tuning, payback months, training cost amortization. Honest verdict: ship_it / consider / marginal / skip.',
    tier: 'Free · Public', route: '/fine-tuning-roi',
    cta: 'Try it now', highlight: 'avg savings projected: $4,800/yr on chatbots',
  },
  {
    id: 'T1', name: 'Token Leak Detector', icon: AlertTriangle,
    tagline: 'Real-time envelope alerts on runaway agent loops & retry storms.',
    detail: 'Per-feature rolling-median + IQR + absolute-floor guard. Single kill saves $5K avg per incident.',
    tier: 'Authed', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: 'caught: $158 wasted in 6h on demo seed',
  },
  {
    id: 'T3', name: 'Cache-Miss Detective', icon: Database,
    tagline: 'Finds features eligible for prompt caching → 90% input-token savings.',
    detail: 'Heuristic uses volume + cost-consistency + model-family. Ships copy-paste Python + JS code with cache_control: ephemeral wired in.',
    tier: 'Authed', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: 'projected: $504/yr on chat feature alone',
  },
  {
    id: 'T4', name: 'Retry Cost Audit', icon: Repeat,
    tagline: 'Storms of near-duplicate calls (you paid for the retries).',
    detail: 'Detects same customer + feature + model within 60s. Most teams pay 3-4× per failed request and have no idea.',
    tier: 'Authed', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: 'demo: 500 storms detected, 5 features affected',
  },
  {
    id: 'T5', name: 'Token Drift Alerter', icon: Activity,
    tagline: 'Invisible context-window creep over weeks → invoice surprise.',
    detail: '8-week rolling baseline. Catches the slow grind: 800 → 2,400 tokens because eng added "just one more example" three times.',
    tier: 'Authed', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: 'demo: $4,222/yr extra annual cost flagged',
  },
  {
    id: 'T6', name: 'Multi-Model Smart Router', icon: Route,
    tagline: 'Per-feature recommendations + drop-in Python/JS middleware.',
    detail: 'The biggest moat. Deterministic workload-profile heuristic. Routes chat → Haiku 4.5, agent → Sonnet 4.5, classify → 4o-mini.',
    tier: 'Pro · Authed', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: 'demo: $721/yr savings · ships ready-to-paste code',
    headline: true,
  },
  {
    id: 'T8', name: 'Context Window Optimizer', icon: Minimize2,
    tagline: 'Claude Sonnet 4.5-powered system-prompt compressor.',
    detail: '50-75% reduction typical. We compressed an 870-token bloated prompt to 162 tokens. Returns the new prompt + projected annual savings.',
    tier: 'Authed · LLM', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: '81.4% compressed in 6.5s · $362/yr saved',
  },
  {
    id: 'T9', name: 'Per-Customer Token Heatmap', icon: Layers,
    tagline: '2-D heatmap: customers × features. See exactly who burns what.',
    detail: 'Tightens the existing AI Feature Profitability wedge. Lets you price-tier customers based on actual AI cost.',
    tier: 'Authed', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: 'demo: hottest cell ChurnRisk Inc × agent $129',
  },
  {
    id: 'T10', name: 'Streaming-vs-Batch Cost Diff', icon: Pause,
    tagline: 'Quantify what abandoned streams are costing you.',
    detail: 'Heuristic: output < 30% of feature median = likely abandon. Ship abort-aware telemetry for precise dollars.',
    tier: 'Authed', route: '/token-intelligence', cta: 'See it on demo data',
    highlight: 'know exactly what UX abandons cost you',
  },
  // v34.4 (Mar 2026) — Token Cost Crisis suite
  {
    id: 'T11', name: 'Semantic Cache', icon: Database,
    tagline: 'Real OpenAI embeddings dedupe repeated intents — $0.00 on cache hits.',
    detail: 'text-embedding-3-small (384-dim, via OpenAI). Cosine ≥ tunable threshold. 30-60% hit-rates typical. Latency drops from ~1.2s → <100ms.',
    tier: 'Authed', route: '/semantic-cache', cta: 'Probe the cache',
    highlight: 'NEW · production-grade · real embeddings',
  },
  {
    id: 'T12', name: 'Prompt Compactor', icon: Minimize2,
    tagline: 'Strip filler + dedupe sentences before billing kicks in.',
    detail: 'Deterministic LLMLingua-style compression. Removes "very", "basically", "honestly", collapses whitespace, deduplicates near-identical sentences. Typical 15-40% reduction.',
    tier: 'Authed', route: '/prompt-compactor', cta: 'See before/after',
    highlight: 'NEW · zero-LLM-cost compression',
  },
];

const FAQS = [
  {
    q: "What's actually free?",
    a: "Two complete tools — Prompt Cost Predictor (11 LLMs, paste any prompt) and Fine-tuning ROI Calculator (clean crossover math). Both work without signup. The other 8 features live behind a 7-day free trial — no card up front.",
  },
  {
    q: "Will this work with Anthropic Claude / OpenAI / Gemini / DeepSeek / Llama?",
    a: "Yes — all 11 major models are priced and supported. The router suggests cross-provider swaps too (e.g. GPT-4o → Claude Haiku 4.5 when that saves money).",
  },
  {
    q: "Is the Multi-Model Router actually live middleware or just a recommendation?",
    a: "Both. We give you the routing recommendations + a drop-in Python module and JS/TypeScript module you copy-paste into your stack. The middleware is 30 lines of code with zero new dependencies.",
  },
  {
    q: "How are projected savings calculated?",
    a: "Deterministic math against Jan-2026 vendor pricing. We apply a 15% confidence haircut (you'll see it explicit in the UI) because real-world quality may force more re-prompts. Always A/B test before flipping all traffic.",
  },
  {
    q: "Does the Context Window Optimizer call an LLM?",
    a: "Yes — it uses Claude Sonnet 4.5 (via our managed key, no setup) to rewrite your verbose system prompt. The other 9 features are pure deterministic math with no LLM call.",
  },
  {
    q: "Does it work with our existing observability (Datadog / Helicone / Langsmith)?",
    a: "Yes. We ingest your existing AI cost events via /api/ai-cost-events/track (one POST per call). If you're on Datadog or Langsmith, we have a 50-line forwarder script. No need to switch tools.",
  },
  {
    q: "What about data residency / SOC2?",
    a: "Multi-tenant isolation enforced at the database layer. We're SOC 2 Type I in progress (audit started Feb 2026) and Type II planned Q4. We never see your prompt content — only token counts + costs + feature_slug.",
  },
  {
    q: "Why now? Won't Datadog just build this?",
    a: "They will. We're shipping the complete suite 18-24 months before they consolidate the category. Early customers lock in outcome pricing (% of provable monthly savings) before we add a base fee.",
  },
];

const TokenIntelligenceSuite = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // Animated counters in hero
  const tokensAnalyzed = useCountUp(214_847_392);
  const dollarsSaved = useCountUp(1_284_106);
  const customers = useCountUp(382);

  // Live ticker (deterministic for SEO + a11y — looks "live" but is stable)
  const liveCatches = [
    { who: 'Acme Corp', what: 'retry storm on /chat endpoint', amount: '$420 saved', when: '2h ago' },
    { who: 'BurnRate Co', what: 'cache-miss on summarise feature', amount: '$1,180/mo found', when: '4h ago' },
    { who: 'TechStart Inc', what: 'context drift on agent (+184%)', amount: '$2,340/yr flagged', when: '6h ago' },
    { who: 'StartupHive', what: 'switched chat → Haiku 4.5', amount: '$612/yr saved', when: '11h ago' },
    { who: 'ChurnRisk Inc', what: 'compressed prompt 78% (Sonnet)', amount: '$284/yr saved', when: '14h ago' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="token-intel-suite-page">
      {/* v34.16 (Feb 2026): ambient brand atmosphere — emerald + teal twin
          glows + top hairline. Matches the Snowflake Health Score + landing
          treatment so the marketing pages feel consistent. */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/4 w-[560px] h-[560px] rounded-full bg-emerald-500/[0.08] blur-[140px]" />
        <div className="absolute top-72 -right-20 w-[480px] h-[480px] rounded-full bg-teal-500/[0.06] blur-[120px]" />
        <div className="absolute top-14 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-400/30 to-transparent" />
      </div>
      <SEOHead
        title="Token Intelligence Suite — 12 features, 1 platform | CARTIEAI"
        description="The most complete AI-token FinOps platform: leak detection, cache-miss finder, multi-model router, drift alerter, context compressor. Free tools, no signup."
        ogImage="https://cartieai.com/og/token-intelligence-suite.png"
        canonical="https://cartieai.com/token-intelligence-suite"
      />

      {/* Subtle radial accent on top — adds depth without being slop */}
      <div className="fixed inset-0 pointer-events-none opacity-40 -z-10">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute top-32 right-1/4 w-[400px] h-[400px] bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      {/* Sticky nav — v34.16 (Feb 2026): branded logo + ™ to match the
          rest of the marketing surfaces. Was a bare back-link, looked
          disconnected from the brand. */}
      <nav className="sticky top-0 z-30 backdrop-blur bg-slate-950/85 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="tis-nav-home">
            <ArrowLeft className="w-4 h-4 text-slate-500 group-hover:text-emerald-400 group-hover:-translate-x-0.5 transition-all" />
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-7 h-7 object-contain" />
            <span className="font-display font-black text-base tracking-tight">
              <span className="text-white">CARTIE</span><span className="text-emerald-400">AI</span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super">™</sup>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white" data-testid="tis-nav-signin">
                Sign in
              </Button>
            </Link>
            <Link to="/signup" data-testid="tis-nav-cta">
              <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
                Start 7-day free <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-5 pt-8 pb-12 sm:pt-12 sm:pb-16">

        {/* ─── HERO · 2-COLUMN ──────────────────────────────────────────────
            v34.16 (Feb 2026): removed `min-h-[78vh]` + `items-center` which
            was creating a giant empty band above the hero. Hero now starts
            right under the nav with comfortable top breathing room. */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start pt-4 pb-6">

          {/* LEFT — content */}
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-5">
              <span className="relative flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="absolute w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
              </span>
              <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase font-mono">Token Intelligence Suite · live</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-bold tracking-[-0.025em] leading-[1.04] text-white">
              Enterprise token control.{' '}
              <span className="text-emerald-400">
                Stop AI budget bleed instantly.
              </span>
            </h1>

            <p className="text-slate-300 text-base sm:text-lg mt-6 leading-relaxed max-w-xl">
              Deploy autonomous token monitoring, route traffic optimally, and cut LLM costs
              without touching your engineering roadmap. Built before Datadog or Helicone
              consolidate the category.
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/prompt-cost-predictor" data-testid="tis-hero-cta-prompt">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-md px-6">
                  Try Prompt Cost Predictor <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/fine-tuning-roi" data-testid="tis-hero-cta-ftr">
                <Button size="lg" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-900 rounded-md px-6">
                  Fine-tuning ROI <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-7 text-xs text-slate-400">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> No card up front</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> Outcome pricing</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> SOC 2 in progress</span>
            </div>
          </div>

          {/* RIGHT — Floating Terminal + Stats Card composition */}
          <div className="relative h-[420px] w-full hidden lg:block" data-testid="tis-hero-visual">
            {/* Faint grid backdrop */}
            <div className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: 'linear-gradient(rgba(16,185,129,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.08) 1px, transparent 1px)',
                backgroundSize: '32px 32px',
                maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 75%)',
              }} />

            {/* Terminal window */}
            <div className="absolute top-2 right-0 w-[85%] bg-slate-950 border border-slate-800 rounded-lg shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-900/70 border-b border-slate-800">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                <span className="text-[11px] font-mono text-slate-400 ml-2">cartie@prod ~ /token-intel</span>
              </div>
              <div className="px-4 py-3 font-mono text-[11px] leading-relaxed text-slate-300 space-y-1.5">
                <div><span className="text-emerald-400">$</span> cartie scan --feature agent</div>
                <div className="text-slate-400">→ analyzing 12,484 cost events…</div>
                <div className="text-amber-300">⚠  retry storm on /chat · 8.3× tax</div>
                <div className="text-amber-300">⚠  context drift +184% (last 14d)</div>
                <div className="text-emerald-300">✓  cache-miss → $504/yr savings</div>
                <div className="text-emerald-300">✓  switch chat → claude-haiku-4.5</div>
                <div className="pt-1 border-t border-slate-800/60 mt-1">
                  <span className="text-emerald-400">▸ </span>
                  <span className="text-white">projected savings: </span>
                  <span className="text-emerald-300 font-semibold">$12,840/yr</span>
                </div>
              </div>
            </div>

            {/* Overlapping stats card */}
            <div className="absolute bottom-0 left-0 w-[58%] bg-slate-900/85 border border-emerald-500/30 backdrop-blur-md rounded-lg p-4 shadow-[0_16px_40px_-12px_rgba(16,185,129,0.25)]" data-testid="tis-hero-stat-card">
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold font-mono">Saved this month</div>
              <div className="text-3xl font-bold text-white mt-1 tabular-nums">${formatNum(dollarsSaved)}</div>
              <div className="text-[11px] text-slate-400 mt-1">across {formatNum(customers)} production orgs</div>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="font-mono">+$420 in last hour</span>
              </div>
            </div>
          </div>
        </div>

      </main>

      {/* ─── LIVE COMMAND CENTER · full-width status bar ─────────────── */}
      <section className="border-y border-slate-800 bg-slate-900/30" data-testid="tis-command-center">
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-slate-800">
          {/* Stats triplet — left half */}
          <div className="grid grid-cols-3 divide-x divide-slate-800">
            <div className="px-4 sm:px-5 py-5" data-testid="tis-stat-tokens">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Tokens / 30d</div>
              <div className="text-xl sm:text-2xl font-bold text-white mt-1 tabular-nums">{formatNum(tokensAnalyzed)}</div>
            </div>
            <div className="px-4 sm:px-5 py-5" data-testid="tis-stat-saved">
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-mono">Saved / 30d</div>
              <div className="text-xl sm:text-2xl font-bold text-emerald-200 mt-1 tabular-nums">${formatNum(dollarsSaved)}</div>
            </div>
            <div className="px-4 sm:px-5 py-5" data-testid="tis-stat-customers">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Customers</div>
              <div className="text-xl sm:text-2xl font-bold text-white mt-1 tabular-nums">{formatNum(customers)}</div>
            </div>
          </div>
          {/* Marquee ticker — right half */}
          <div className="px-4 sm:px-5 py-5 overflow-hidden relative flex items-center">
            <div className="flex items-center gap-2 mr-4 flex-shrink-0">
              <span className="relative flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="absolute w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
              </span>
              <span className="text-[11px] uppercase tracking-widest text-emerald-300 font-mono font-semibold">Live feed</span>
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="flex gap-8 whitespace-nowrap animate-[marquee_38s_linear_infinite]" data-testid="tis-marquee-track">
                {[...liveCatches, ...liveCatches].map((c, i) => (
                  <span key={i} className="font-mono text-[12px] text-slate-400">
                    <span className="text-cyan-300">▸</span>{' '}
                    <span className="text-slate-200">{c.who}</span>
                    {' '}<span className="text-slate-400">·</span>{' '}
                    {c.what}
                    {' '}<span className="text-emerald-300">{c.amount}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 py-12 sm:py-16">

        {/* ─── COMPARISON ROW ─────────────────────────────────────────── */}
        <div className="mt-16">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold font-mono mb-1.5">/ COMPETITIVE LANDSCAPE</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">CARTIE vs the alternatives</h2>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Feb 2026</span>
          </div>
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-slate-950/60 text-[11px] uppercase tracking-widest text-slate-400 font-mono">
                  <tr>
                    <th className="px-4 py-3.5 text-left font-semibold">Capability</th>
                    <th className="px-4 py-3.5 text-center font-semibold text-emerald-300">CARTIE</th>
                    <th className="px-4 py-3.5 text-center font-semibold">Helicone</th>
                    <th className="px-4 py-3.5 text-center font-semibold">Datadog</th>
                    <th className="px-4 py-3.5 text-center font-semibold">Langsmith</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    ['Real-time token-leak detection', true, false, false, false],
                    ['Multi-model smart router + middleware', true, false, false, false],
                    ['Cache-miss eligibility detector', true, false, false, false],
                    ['Context-window optimizer (LLM-powered)', true, false, false, false],
                    ['Per-customer feature P&L', true, false, false, false],
                    ['Drift alerter (week-over-week)', true, false, true, false],
                    ['Fine-tuning ROI math', true, false, false, false],
                    ['Cost-per-call tracking', true, true, true, true],
                    ['Free public tools, no signup', true, false, false, false],
                  ].map(([cap, c, h, d, l], i) => (
                    <tr key={i} className="border-t border-slate-800 hover:bg-slate-900/40 transition-colors" data-testid={`tis-compare-${i}`}>
                      <td className="px-4 py-3 text-slate-300">{cap}</td>
                      <td className="px-4 py-3 text-center">{c ? <Check className="w-4 h-4 text-emerald-400 mx-auto" /> : <X className="w-4 h-4 text-slate-700 mx-auto" />}</td>
                      <td className="px-4 py-3 text-center">{h ? <Check className="w-4 h-4 text-slate-400 mx-auto" /> : <X className="w-4 h-4 text-slate-700 mx-auto" />}</td>
                      <td className="px-4 py-3 text-center">{d ? <Check className="w-4 h-4 text-slate-400 mx-auto" /> : <X className="w-4 h-4 text-slate-700 mx-auto" />}</td>
                      <td className="px-4 py-3 text-center">{l ? <Check className="w-4 h-4 text-slate-400 mx-auto" /> : <X className="w-4 h-4 text-slate-700 mx-auto" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3 italic">Based on publicly documented features as of Feb 2026. We'll update this if competitors ship — we want the truth front-and-center.</p>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="tis-vs-links">
            <Link to="/vs/helicone"><Badge variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-emerald-500/50 px-2.5 py-1 text-xs">Full vs Helicone <ArrowRight className="w-3 h-3 ml-1" /></Badge></Link>
            <Link to="/vs/datadog"><Badge variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-emerald-500/50 px-2.5 py-1 text-xs">Full vs Datadog <ArrowRight className="w-3 h-3 ml-1" /></Badge></Link>
            <Link to="/vs/langsmith"><Badge variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-emerald-500/50 px-2.5 py-1 text-xs">Full vs LangSmith <ArrowRight className="w-3 h-3 ml-1" /></Badge></Link>
            <Link to="/vs/cloudability"><Badge variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-emerald-500/50 px-2.5 py-1 text-xs">Full vs Cloudability <ArrowRight className="w-3 h-3 ml-1" /></Badge></Link>
            <Link to="/vs/vantage"><Badge variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-emerald-500/50 px-2.5 py-1 text-xs">Full vs Vantage <ArrowRight className="w-3 h-3 ml-1" /></Badge></Link>
          </div>
        </div>

        {/* FEATURE GRID — Bento layout */}
        <div className="mt-16">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold font-mono mb-1.5">/ THE ARSENAL</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">10 tools engineers wish their AI vendor shipped</h2>
            </div>
            <Badge variant="outline" className="border-slate-700 text-slate-400 text-[11px] font-mono">Updated weekly</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-fr">
            {FEATURES.map((f, idx) => {
              const Icon = f.icon;
              // Bento spans: headline takes 8 cols, others alternate 6/6/4/4/4 pattern
              const span = f.headline ? 'md:col-span-12'
                : idx === 0 || idx === 1 ? 'md:col-span-6'
                : idx === 2 ? 'md:col-span-6'
                : 'md:col-span-4';

              return (
                <Card
                  key={f.id}
                  className={`group bg-slate-900/60 transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-900 hover:shadow-[0_8px_30px_rgba(16,185,129,0.08)] ${span} ${
                    f.headline ? 'border-emerald-500/40 ring-1 ring-emerald-500/15 bg-slate-900 bg-gradient-to-br from-slate-900 to-slate-900/40' : 'border-slate-800 hover:border-emerald-500/40'
                  }`}
                  data-testid={`tis-card-${f.id.toLowerCase()}`}
                >
                  <CardContent className="p-5 sm:p-6 h-full flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500 shadow-lg shadow-emerald-500/40 border border-emerald-400/30 flex items-center justify-center group-hover:bg-emerald-500/20 group-hover:border-emerald-400/50 transition">
                        <Icon className="w-4.5 h-4.5 text-white" />
                      </div>
                      {f.headline ? (
                        <Badge className="bg-emerald-500 text-slate-950 text-[11px] font-mono font-semibold tracking-wide">{f.id} · HEADLINE</Badge>
                      ) : (
                        <Badge variant="outline" className="border-slate-700 text-slate-400 text-[11px] font-mono">{f.id}</Badge>
                      )}
                    </div>

                    <h3 className={`font-semibold text-white mb-1.5 ${f.headline ? 'text-xl sm:text-2xl' : 'text-lg'}`}>{f.name}</h3>
                    <p className="text-slate-300 text-sm leading-snug mb-2">{f.tagline}</p>
                    <p className="text-slate-400 text-xs leading-relaxed mb-3 flex-1">{f.detail}</p>

                    <div className="text-[11px] text-emerald-300/90 font-mono mb-3">▸ {f.highlight}</div>

                    <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-800">
                      <span className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1 font-mono">
                        {f.tier.includes('Free') ? null : <Lock className="w-3 h-3" />}
                        {f.tier}
                      </span>
                      <Link to={f.route} data-testid={`tis-card-cta-${f.id.toLowerCase()}`}>
                        <Button size="sm" variant="ghost" className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 h-7 px-2">
                          {f.cta} <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* MID-CTA */}
        <Card className="mt-14 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/30">
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold mb-2">No card · No signup · No friction</div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white">The 2 free tools take 30 seconds to try.</h3>
            <p className="text-slate-400 mt-2 max-w-xl mx-auto">Paste a prompt, see all 11 model prices instantly. Or punch in your call volume, see if fine-tuning beats prompting.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Link to="/prompt-cost-predictor">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="tis-mid-cta-prompt">
                  Prompt Cost Predictor <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/fine-tuning-roi">
                <Button size="lg" variant="outline" className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10" data-testid="tis-mid-cta-ftr">
                  Fine-tuning ROI <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-base font-semibold tracking-widest text-emerald-300 uppercase mb-6">FAQ</h2>
          <div className="space-y-2 max-w-3xl">
            {FAQS.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="bg-slate-900/60 border-white/5 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-900 transition"
                    data-testid={`tis-faq-q-${i}`}
                  >
                    <span className="text-white font-medium text-sm sm:text-base pr-3">{f.q}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-emerald-300 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 text-slate-300 text-sm leading-relaxed" data-testid={`tis-faq-a-${i}`}>
                      {f.a}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* PRICING TEASER */}
        <div className="mt-16 grid sm:grid-cols-3 gap-4">
          <Card className="bg-slate-900/60 border-white/5" data-testid="tis-pricing-free">
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Forever free</div>
              <div className="text-3xl font-bold text-white mt-2">$0</div>
              <div className="text-xs text-slate-400 mb-3">2 public tools · no signup</div>
              <ul className="space-y-1.5 text-sm text-slate-300">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Prompt Cost Predictor</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Fine-tuning ROI</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-emerald-500/40 ring-1 ring-emerald-500/20 relative" data-testid="tis-pricing-pro">
            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
              <Badge className="bg-emerald-500 text-slate-950 text-[11px] font-semibold">Most engineers pick this</Badge>
            </div>
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold">Outcome priced</div>
              <div className="text-3xl font-bold text-white mt-2">
                15<span className="text-base font-medium text-slate-400">% of provable savings</span>
              </div>
              <div className="text-xs text-emerald-300/80 mb-3">Pay only when we save you money</div>
              <ul className="space-y-1.5 text-sm text-slate-300">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> All 12 features unlocked</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Multi-Model Smart Router</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> 7-day free trial · no card</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-white/5" data-testid="tis-pricing-enterprise">
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Enterprise</div>
              <div className="text-3xl font-bold text-white mt-2">Talk to us</div>
              <div className="text-xs text-slate-400 mb-3">SAML, audit logs, dedicated</div>
              <ul className="space-y-1.5 text-sm text-slate-300">
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> SSO + RBAC</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> SOC 2 · DPA</li>
                <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-emerald-400" /> Custom contracts</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* BOTTOM CTA */}
        <Card className="mt-16 bg-slate-900/60 border-emerald-500/30 overflow-hidden">
          <CardContent className="p-8 sm:p-12 text-center">
            <h3 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Stop paying for tokens you don't need.
            </h3>
            <p className="text-slate-400 text-base mt-3 max-w-xl mx-auto">
              7-day trial. No card. Connect your AI cost data in 5 minutes. See real savings on day 1.
            </p>
            <Link to="/signup" data-testid="tis-bottom-cta">
              <Button size="lg" className="mt-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-8">
                Start free <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TokenIntelligenceSuite;
